import { Button, Container, Grid, Typography} from '@mui/material'
import { useTranslation } from "react-i18next"

import Spacer from "../../../atoms/Spacer/Spacer"

import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"
import styles from './SubscriptionGiftCheckStatusPage.module.css'
import { useContext, useEffect, useState } from 'react'
import { AppContext } from '../../../../stores/App/AppStore'
import { useSubscriptionGiftRepository } from '../../../../repositories/SubscriptionGift/use-subscription-gift-repository'
import CustomInput from '../../../atoms/CustomInput/CustomInput'
import GtmEventName from '../../../../helpers/analytics/GtmEventName'
import { logLandingPageAction } from '../../../../clients/logger'
import useGoogleTagManager from '../../../../helpers/analytics/use-google-tag-manager'
import { subscriptionStatusPageCopyShareCodeMessageTemplate } from '../../../../helpers/logging/templates'

const SubscriptionGiftCheckStatusPage = () => {

    const { t } = useTranslation()
    const { trackButtonClick } = useGoogleTagManager()
    const { state: appState } = useContext(AppContext)
    const [code, setCode] = useState(``)
    const [hasFetched, setHasFetched] = useState(false)
    const [subscriptionPeriod, setSubscriptionPeriod] = useState(0)
    const [recipientName, setRecipientName] = useState(``)
    const [recipientMobile, setRecipientMobile] = useState(``)
    const [subscriptionStatus, setSubscriptionStatus] = useState(``)
    const [subscriptionGiftCardUrl, setSubscriptionGiftCardUrl] = useState(``)
    const { retrieveGiftBanners, retrieveSubscriptionGift } = useSubscriptionGiftRepository()

    useTrackPageView(`Subscription Gift Check Status Page`)

    useEffect(() => {
        if (!appState.giftBanners){
            retrieveGiftBanners()
        }
    }, [])

    const handleCheckButtonPress = () => {
        trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_CHECK_STATUS_SUBMIT)
        logLandingPageAction(`User pressed check status button on subscription check status page`, `myInfo`)
        setHasFetched(true)
        retrieveSubscriptionGift(code).then(subscriptionGift => {
            setSubscriptionPeriod(subscriptionGift.subscriptionPeriod ?? 0)
            setRecipientName(subscriptionGift.recipientName ?? ``)
            setRecipientMobile(subscriptionGift.recipientPhoneNumber ?? ``)
            setSubscriptionGiftCardUrl(subscriptionGift.giftCardImageURL ?? ``)
            switch (subscriptionGift.status){
                case `PENDING_PAYMENT`:
                    setSubscriptionStatus(`Pending Payment`)
                    break
                case `PENDING_REDEMPTION`:
                    setSubscriptionStatus(`Pending Redemption`)
                    break
                case `REDEEMED`:
                    setSubscriptionStatus(`Redeemed`)
                    break
                default:
                    setSubscriptionStatus(`Error`)
            }
        })
    }

    const handleResetButtonPress = () => {
        setHasFetched(false)
        setCode(``)
    }

    const handleShareButtonPress = async () => {
        if (subscriptionPeriod > 0){
            try {
                if(code){
                    const link = `${process.env.REACT_APP_STARTER_DOMAIN_NAME}/subscription-gift/redemption-landing/${code}`
                    await navigator.clipboard.writeText(`${link}`)
                    if (navigator.share) {
                        try {
                            await navigator.share({
                                title: `Subscription Gift Redemption Link`,
                                text: `${link}`,
                            })
                            trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_REDEMPTION_SHARE_CLICK, `${link}`)
                            logLandingPageAction(subscriptionStatusPageCopyShareCodeMessageTemplate(`${code}`), `myInfo`)
                        } catch (err) {
                            logLandingPageAction(`Encountered error while sharing subscription gift code: ${code}, error: ${err}`, `myInfo`)
                        }
                    } else {
                        alert(`Sharing not supported on this device`)
                    }
                }
            } catch (err) {
                console.error(`Failed to copy: `, err)
            }
        }
    }

    const GiftForm = <Container>
        <Container className={styles.imgContainer}>
            <img className={styles.imgBanner} src={appState.giftBanners?.subscriptionStatusFormBanner} alt="Gift a subscription status Banner" />
        </Container>
        <Spacer height={48} />
        <Typography variant="h3">{t(`subscription_gift_status_header`)}</Typography>
        <Spacer height={24} />
        <CustomInput
            fullWidth
            label={t(`enter_code`)}
            onChange={setCode}
            value={code}
            eagerChange
            placeholder='Your code is in the email we sent you.'
            isSignUp={false}
        />
        <Spacer height={24} />
        <Button variant='contained' onClick={handleCheckButtonPress} fullWidth>{t(`subscription_gift_check_status_button`)}</Button>
    </Container>

    const GiftStatus = <Container>
        <Container className={styles.body}>
            <img className={styles.giftImgBanner} src={subscriptionGiftCardUrl} alt="Gift a subscription status Details Banner" />
            <Spacer height={24} />
            <Typography variant="h3">{t(`subscription_gift_status_header`)}</Typography>
            <Spacer height={24} />
            <Grid container direction="row" className={styles.contentGrid}>
                <Typography variant={`h6`} className={styles.header}>
                    {t(`subscription_gift_details_subscription_period`)}
                </Typography>
                <Typography variant={`body1`}>{subscriptionPeriod + ` years`}</Typography>
            </Grid>
            <Grid container direction="row" className={styles.contentGrid}>
                <Typography variant={`h6`} className={styles.header}>
                    {t(`name`)}
                </Typography>
                <Typography variant={`body1`}>{recipientName}</Typography>
            </Grid>
            <Grid container direction="row" className={styles.contentGrid}>
                <Typography variant={`h6`} className={styles.header}>
                    {t(`mobile`)}
                </Typography>
                <Typography variant={`body1`}>{recipientMobile}</Typography>
            </Grid>
            <Grid container direction="row" className={styles.contentGrid}>
                <Typography variant={`h6`} className={styles.header}>
                    {t(`status`)}
                </Typography>
                <Typography variant={`body1`}>{subscriptionStatus}</Typography>
            </Grid>
            <Spacer height={24} />
            <Button variant='contained' onClick={handleShareButtonPress} fullWidth>{t(`share`)}</Button>
            <Spacer height={24} />
            <Button variant='outlined' onClick={handleResetButtonPress} fullWidth>{t(`Back`)}</Button>
        </Container>
    </Container>

    return (<>
        {!hasFetched ? GiftForm : GiftStatus}
    </>
    )
}

export default SubscriptionGiftCheckStatusPage